<template>
  <div v-if="isOpen" class="file-viewer-modal-overlay" @click.self="closeModal" @contextmenu.prevent>
    <div class="file-viewer-modal" @contextmenu.prevent>
      <!-- Header -->
      <div class="file-viewer-header">
        <div class="d-flex gap-1 align-items-center" style="min-width: 0; flex: 1;">
          <i :class="getFileIcon()" class="font-size-18"></i>
          
          <div class="d-flex flex-column" style="min-width: 0;">
            <div class="file-viewer-title">
              <span>{{ fileName }}</span>
            </div>
            <span v-if="fileDescription" class="file-viewer-description">{{ fileDescription }}</span>
            <span v-if="page" class="file-viewer-page-indicator">Page {{ page }}</span>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button 
            class="file-viewer-details-btn" 
            @click="isShowDetails = !isShowDetails"
            :title="isShowDetails ? 'Hide Details' : 'Show Details'"
          >
            <i class="mdi mdi-information-outline"></i>
          </button>
          <button class="file-viewer-close-btn" @click="closeModal" aria-label="Close">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="file-viewer-body">
        <div class="file-viewer-content">
          <!-- Loading State -->
          <div v-if="loading" class="file-viewer-loading">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3">Loading file...</p>
            <div v-if="downloadProgress > 0" class="progress-container">
              <div class="progress">
                <div 
                  class="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  :style="{ width: downloadProgress + '%' }"
                  :aria-valuenow="downloadProgress" 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {{ downloadProgress }}%
                </div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="file-viewer-error">
            <i class="mdi mdi-alert-circle-outline text-danger"></i>
            <p class="mt-3 text-danger">{{ error }}</p>
            <button class="btn btn-primary mt-3" @click="loadFile">
              <i class="mdi mdi-refresh text-white"></i>
            </button>
          </div>

          <!-- PDF Viewer (Classic vue-pdf-app/PDF.js Theme) -->
          <div v-else-if="isPDF && fileUrl" class="file-viewer-pdf">
            <!-- Classic top toolbar -->
            <div class="pdf-top-toolbar">
              <!-- Left: Page controls -->
              <div class="toolbar-group">
                <button 
                  @click="prevPage" 
                  :disabled="viewMode === 'scroll' || currentPage <= 1"
                  class="toolbar-btn" 
                  title="Previous Page"
                >
                  <i class="mdi mdi-chevron-up"></i>
                </button>
                <button 
                  @click="nextPage" 
                  :disabled="viewMode === 'scroll' || currentPage >= totalPages"
                  class="toolbar-btn me-2" 
                  title="Next Page"
                >
                  <i class="mdi mdi-chevron-down"></i>
                </button>
                
                <div class="page-counter-container" v-if="viewMode === 'single'">
                  <input 
                    type="number" 
                    v-model.number="pageInput" 
                    @keydown.enter="onPageInputChange"
                    @blur="onPageInputChange"
                    class="page-input"
                    min="1"
                    :max="totalPages"
                  />
                  <span class="page-slash">/</span>
                  <span class="total-pages">{{ totalPages }}</span>
                </div>
                <div class="page-counter-container scroll-indicator" v-else>
                  <span class="total-pages">All Pages ({{ totalPages }})</span>
                </div>
              </div>

              <!-- Center: Zoom controls -->
              <div class="toolbar-group zoom-group">
                <button @click="zoomOut" class="toolbar-btn" title="Zoom Out">
                  <i class="mdi mdi-minus"></i>
                </button>
                <div class="zoom-indicator">{{ Math.round(scale * 100) }}%</div>
                <button @click="zoomIn" class="toolbar-btn" title="Zoom In">
                  <i class="mdi mdi-plus"></i>
                </button>

                <span class="vertical-divider"></span>

                <button @click="fitToWidth" class="toolbar-btn" title="Fit to Width">
                  <i class="mdi mdi-arrow-expand-horizontal"></i>
                </button>
                <button @click="fitToPage" class="toolbar-btn" title="Fit to Page">
                  <i class="mdi mdi-arrow-expand-vertical"></i>
                </button>
              </div>

              <!-- Right: Actions -->
              <div class="toolbar-group actions-group">
                <!-- Print -->
                <button @click="printFile" class="toolbar-btn" title="Print PDF">
                  <i class="mdi mdi-printer"></i>
                </button>

                <!-- Download -->
                <button @click="downloadFile" class="toolbar-btn" title="Download PDF">
                  <i class="mdi mdi-download"></i>
                </button>

                <span class="vertical-divider"></span>

                <!-- Rotate -->
                <button @click="rotateRight" class="toolbar-btn" title="Rotate Clockwise">
                  <i class="mdi mdi-rotate-right"></i>
                </button>

                <!-- Toggle Continuous Scroll -->
                <button @click="toggleViewMode" class="toolbar-btn" :title="viewMode === 'scroll' ? 'Page-by-Page View' : 'Continuous Scroll View'">
                  <i :class="viewMode === 'scroll' ? 'mdi mdi-book-open-page-variant' : 'mdi mdi-file-document-multiple'"></i>
                </button>
              </div>
            </div>

            <!-- Scrollable Workspace -->
            <div class="pdf-scroll-area">
              <VuePdfEmbed
                :key="fileUrl"
                ref="pdfRef"
                :source="fileUrl"
                :page="viewMode === 'single' ? debouncedPage : null"
                :scale="debouncedScale"
                :rotation="debouncedRotation"
                @loaded="onPdfLoaded"
                @loading-failed="onPdfError"
                class="pdf-document"
              />
            </div>
          </div>

          <!-- Image Viewer -->
          <div v-else-if="isImage && fileUrl" class="file-viewer-image">
            <CustomViewer
              :loading="false"
              :isShowDetails="isShowDetails"
              :images="[{ source: fileUrl, fileName: fileName }]"
              style="height: 100%; width: 100%;"
            />
          </div>

          <!-- Unsupported File Type -->
          <div v-else-if="!loading && !isPDF && !isImage" class="file-viewer-unsupported">
            <i class="mdi mdi-file-alert-outline"></i>
            <p class="mt-3">Preview not available for this file type</p>
            <button class="btn btn-primary mt-3" @click="downloadFile">
              <i class="mdi mdi-download"></i> Download File
            </button>
          </div>
        </div>

        <!-- Details Side Panel -->
        <div v-if="isShowDetails" class="file-viewer-details-panel">
          <h6 class="details-panel-title">Document Details</h6>

          <div class="details-panel-content">
            <div class="detail-item">
              <span class="detail-label">Name</span>
              <span class="detail-value">{{ fileName }}</span>
            </div>
            <div v-if="fileDescription" class="detail-item">
              <span class="detail-label">Description</span>
              <span class="detail-value">{{ fileDescription }}</span>
            </div>
            <div v-if="page" class="detail-item">
              <span class="detail-label">Page</span>
              <span class="detail-value">{{ page }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">File Type</span>
              <span class="detail-value text-uppercase">{{ fileType || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Folder Path</span>
              <span 
                class="detail-value detail-link" 
                @click="navigateToFolder"
                title="Go to folder"
              >
                <i class="mdi mdi-folder-outline me-1"></i>{{ folderPath }}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Location Path</span>
              <span 
                class="detail-value detail-link" 
                @click="navigateToLocation"
                title="Go to location"
              >
                <i class="mdi mdi-map-marker-outline me-1"></i>{{ locationPath }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "vue-pdf-embed/src/styles/annotation-layer.css";
import "vue-pdf-embed/src/styles/text-layer.css";
import { mapActions } from 'vuex';
import Loader from "@/components/layout/loader";
import CustomViewer from "@/components/layout/custom-viewer";
import debounce from 'lodash/debounce';

export default {
  name: 'SingleFileViewerModal',
  
  components: {
    VuePdfEmbed: () => ({
      component: import(/* webpackChunkName: "pdf-viewer" */ "vue-pdf-embed/dist/vue2-pdf-embed"),
      loading: Loader
    }),
    CustomViewer
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    citation: {
      type: Object,
      default: null
    }
  },
  
  data() {
    return {
      loading: false,
      error: null,
      fileUrl: null,
      fileBlob: null,
      downloadProgress: 0,
      isShowDetails: false,

      // vue-pdf-embed parameters
      currentPage: 1,
      pageInput: 1,
      totalPages: 1,
      scale: 0.7,
      rotation: 0,
      viewMode: 'scroll', // 'scroll' or 'single'

      // Debounced properties to prevent canvas flickering / rendering collision on rapid clicking
      debouncedScale: 0.7,
      debouncedRotation: 0,
      debouncedPage: 1
    };
  },
  
  created() {
    this.updateDebouncedScale = debounce((val) => {
      this.debouncedScale = val;
    }, 250);
    this.updateDebouncedRotation = debounce((val) => {
      this.debouncedRotation = val;
    }, 250);
    this.updateDebouncedPage = debounce((val) => {
      this.debouncedPage = val;
    }, 250);
  },
  
  computed: {
    fileName() {
      return this.citation?.FileManagerName || 'Unknown File';
    },

    fileDescription() {
      return this.citation?.FileManagerDescription || '';
    },
    
    documentId() {
      return this.citation?.DocumentId;
    },

    locationPath(){
      return this.citation?.Location || '';
    },

    locationId() {
      return this.citation?.LocationId || 0;
    },

    folderPath() {
      return this.citation?.FolderLocation || '';
    },

    folderId() {
      return this.citation?.FolderId || 0;
    },
    
    page() {
      return this.citation?.Page;
    },
    
    fileType() {
      const fileName = this.citation?.FileName || '';
      const extension = fileName.includes('.') 
        ? fileName.split('.').pop().toLowerCase()
        : '';
      return extension;
    },
    
    isPDF() {
      return this.fileType === 'pdf';
    },
    
    isImage() {
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(this.fileType);
    },
  },
  
  watch: {
    isOpen(newValue) {
      if (newValue && this.documentId) {
        // Reset controls
        this.scale = 0.7;
        this.rotation = 0;
        this.currentPage = 1;
        this.pageInput = 1;
        this.viewMode = 'scroll';
        
        // Also reset debounced properties immediately so there's no stale delay from previous files!
        this.debouncedScale = 0.7;
        this.debouncedRotation = 0;
        this.debouncedPage = 1;
        
        this.loadFile();
      } else if (!newValue) {
        this.cleanup();
      }
    },
    currentPage(newVal) {
      this.pageInput = newVal;
      this.updateDebouncedPage(newVal);
    },
    scale(newVal) {
      this.updateDebouncedScale(newVal);
    },
    rotation(newVal) {
      this.updateDebouncedRotation(newVal);
    }
  },
  
  methods: {
    ...mapActions({
      readFileContent: 'aiChat/fileView/readFileContent'
    }),
    
    async loadFile() {
      if (!this.documentId) {
        this.error = 'File ID is missing';
        return;
      }
      
      this.loading = true;
      this.error = null;
      this.downloadProgress = 0;
      
      try {
        const blob = await this.readFileContent({
          id: this.documentId,
          onProgress: (progressData) => {
            this.downloadProgress = progressData.progress;
          }
        });
        
        this.fileBlob = blob;
        
        // Create object URL for the blob
        if (this.isPDF) {
          this.fileUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        } else {
          this.fileUrl = URL.createObjectURL(blob);
        }
        
        this.loading = false;
        this.downloadProgress = 0;
      } catch (err) {
        console.error('Error loading file:', err);
        this.error = err.response?.data?.message || 'Failed to load file. Please try again.';
        this.loading = false;
        this.downloadProgress = 0;
      }
    },

    // Google Docs style actions
    zoomIn() {
      this.scale = Math.min(this.scale + 0.15, 3.0);
    },
    zoomOut() {
      this.scale = Math.max(this.scale - 0.15, 0.4);
    },
    resetZoom() {
      this.scale = 0.7;
      this.rotation = 0;
    },
    fitToWidth() {
      this.scale = 1.3;
    },
    fitToPage() {
      this.scale = 0.85;
    },
    rotateRight() {
      this.rotation = (this.rotation + 90) % 360;
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'scroll' ? 'single' : 'scroll';
    },
    onPageInputChange() {
      let page = parseInt(this.pageInput, 10);
      if (isNaN(page) || page < 1) {
        page = 1;
      } else if (page > this.totalPages) {
        page = this.totalPages;
      }
      this.currentPage = page;
      this.pageInput = page;
    },
    // Instant PDF Download
    downloadFile() {
      if (!this.fileUrl) return;
      const link = document.createElement('a');
      link.href = this.fileUrl;
      link.download = this.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // Instant PDF Print with timeouts & tab fallbacks
    printFile() {
      if (!this.fileUrl) return;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = this.fileUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.error("Silent print error, opening in new tab:", e);
            window.open(this.fileUrl, '_blank');
          }
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 2000);
        }, 1000); // 1-second timeout
      };
    },
    onPdfLoaded(pdf) {
      this.totalPages = pdf.numPages;
      if (this.page) {
        const pageNum = parseInt(this.page, 10);
        if (pageNum > 0 && pageNum <= this.totalPages) {
          this.currentPage = pageNum;
          this.debouncedPage = pageNum;
          this.viewMode = 'single';
        }
      }
    },
    onPdfError(err) {
      this.error = `Error loading PDF: ${err.message}`;
      this.$toast.error("Failed to render PDF view.");
    },

    navigateToFolder() {
      if (this.folderId) {
        this.closeModal();
        this.$changeFolder(this, this.folderId);
      }
    },

    navigateToLocation() {
      if (this.locationId) {
        this.closeModal();
        this.$changeLocation(this, this.locationId);
      }
    },
    
    closeModal() {
      this.$emit('close');
    },
    
    cleanup() {
      if (this.fileUrl) {
        URL.revokeObjectURL(this.fileUrl);
        this.fileUrl = null;
      }
      this.fileBlob = null;
      this.error = null;
      this.downloadProgress = 0;
      this.isShowDetails = false;
    },
    
    getFileIcon() {
      const iconMap = {
        'pdf': 'mdi mdi-file-pdf-box text-danger',
        'doc': 'mdi mdi-file-word-box text-primary',
        'docx': 'mdi mdi-file-word-box text-primary',
        'xls': 'mdi mdi-file-excel-box text-success',
        'xlsx': 'mdi mdi-file-excel-box text-success',
        'jpg': 'mdi mdi-file-image text-info',
        'jpeg': 'mdi mdi-file-image text-info',
        'png': 'mdi mdi-file-image text-info',
        'gif': 'mdi mdi-file-image text-info',
        'default': 'mdi mdi-file-document'
      };
      
      return iconMap[this.fileType] || iconMap['default'];
    }
  },
  
  beforeDestroy() {
    if (this.updateDebouncedScale?.cancel) this.updateDebouncedScale.cancel();
    if (this.updateDebouncedRotation?.cancel) this.updateDebouncedRotation.cancel();
    if (this.updateDebouncedPage?.cancel) this.updateDebouncedPage.cancel();
    this.cleanup();
  }
};
</script>

<style scoped>
.file-viewer-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10600;
  padding: 20px;
}

.file-viewer-modal {
  background: #3d3d3d;
  border-radius: 8px;
  width: 100%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.file-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: #292929;
  border-radius: 8px 8px 0 0;
}

.file-viewer-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  flex: 1;
  min-width: 0;
}

.file-viewer-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-viewer-description {
  color: #a6b0cf;
  font-weight: 500;
  font-size: 0.68rem;
}

.file-viewer-page-indicator {
  color: #556ee6;
  font-weight: 500;
  font-size: 0.7rem;
}

.file-viewer-details-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 18px;
  cursor: pointer;
  color: #a6b0cf;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.file-viewer-details-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.3);
}

.file-viewer-close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #a6b0cf;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.file-viewer-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.file-viewer-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  border-radius: 0 0 8px 8px;
}

.file-viewer-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3d3d3d;
  position: relative;
}

.file-viewer-loading,
.file-viewer-error,
.file-viewer-unsupported {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #a6b0cf;
}

.file-viewer-loading .spinner-border {
  width: 3rem;
  height: 3rem;
}

.progress-container {
  width: 100%;
  max-width: 400px;
  margin-top: 20px;
}

.progress {
  height: 24px;
  border-radius: 12px;
  background-color: #32394e;
  overflow: hidden;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background-color: #556ee6;
  transition: width 0.3s ease;
}

.file-viewer-error i {
  font-size: 24px;
  color: #f46a6a;
}

.file-viewer-pdf {
  width: 100%;
  height: 100%;
  position: relative;
  background: #474747; /* Standard PDF.js workspace background */
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Classic PDF.js compact top toolbar */
.pdf-top-toolbar {
  background-color: #383838; /* PDF.js classic dark gray */
  background-image: linear-gradient(rgba(82, 82, 82, 0.99), rgba(61, 61, 61, 0.99));
  border-bottom: 1px solid #2a2a2a;
  height: 36px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  z-index: 100;
  width: 100%;
  user-select: none;
}

.toolbar-group {
  display: flex;
  align-items: center;
}

/* Centering pages on rotation or zoom overflow */
.pdf-scroll-area {
  flex: 1;
  overflow: auto;
  padding: 24px 12px;
  text-align: center; /* Centers block items in inline-block mode */
  background: #474747;
  width: 100%;
}

.pdf-document {
  display: inline-block;
  text-align: left; /* Keep document text left aligned */
  max-width: none; /* Allow zoom to expand canvases freely */
}

/* Classic PDF.js paper layout */
::v-deep .vue-pdf-embed__page {
  margin: 0 auto 12px auto; /* Perfect centering */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  border-radius: 0px;
  background: #ffffff;
  display: block;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

::v-deep .vue-pdf-embed__page canvas {
  display: block;
  margin: 0 auto;
}

/* Classic small square buttons */
.toolbar-btn {
  background: transparent;
  color: #cccccc;
  border: 1px solid transparent;
  font-size: 15px;
  width: 26px;
  height: 26px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  cursor: pointer;
  margin: 0 1px;
}

.toolbar-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(0, 0, 0, 0.2);
  color: #ffffff;
}

.toolbar-btn:active:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.15);
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.1);
}

.toolbar-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.zoom-indicator {
  color: #cccccc;
  font-size: 12px;
  font-weight: normal;
  min-width: 48px;
  text-align: center;
}

.vertical-divider {
  width: 1px;
  height: 18px;
  background-color: #2e2e2e;
  border-right: 1px solid #4a4a4a;
  margin: 0 8px;
}

.page-counter-container {
  display: flex;
  align-items: center;
  color: #cccccc;
}

.page-input {
  background-color: #2a2a2a;
  border: 1px solid #1a1a1a;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
  border-radius: 2px;
  color: #ffffff;
  width: 34px;
  height: 20px;
  text-align: center;
  font-size: 12px;
  outline: none;
}

.page-input:focus {
  border-color: #4a4a4a;
  background-color: #3a3a3a;
}

.page-input::-webkit-outer-spin-button,
.page-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.page-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.page-slash {
  margin: 0 4px;
  color: #888888;
  font-size: 11px;
}

.total-pages {
  font-size: 11px;
  color: #cccccc;
}

.scroll-indicator {
  padding: 0 2px;
}

.file-viewer-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

/* Details Side Panel */
.file-viewer-details-panel {
  width: 280px;
  min-width: 280px;
  background: #2a3042;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  overflow-y: auto;
  padding: 16px;
}

.details-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.details-panel-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #a6b0cf;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 13px;
  color: #e9ecef;
  word-break: break-word;
}

.detail-link {
  cursor: pointer;
  color: #556ee6;
  transition: color 0.2s;
  display: flex;
  align-items: flex-start;
}

.detail-link:hover {
  color: #7b93f0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .file-viewer-details-panel {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 10;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
  }

  .file-viewer-body {
    position: relative;
  }
  
  .pdf-top-toolbar {
    height: auto;
    padding: 4px 6px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .zoom-group {
    order: 3;
    width: 100%;
    justify-content: center;
    margin-top: 4px;
  }

  .toolbar-btn {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }

  .vertical-divider {
    margin: 0 4px;
  }
}

@media (max-width: 600px) {
  .pdf-scroll-area {
    padding: 12px 0px !important;
  }
  .pdf-document {
    width: 100% !important;
    max-width: 100% !important;
  }
  ::v-deep .vue-pdf-embed {
    width: 100% !important;
    max-width: 100% !important;
  }
  ::v-deep .vue-pdf-embed__page {
    width: 100% !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  /* Make modal itself expand better on mobile */
  .file-viewer-modal-overlay {
    padding: 0px !important;
  }
  .file-viewer-modal {
    height: 100vh !important;
    max-height: 100vh !important;
    border-radius: 0px !important;
  }
  .file-viewer-header {
    border-radius: 0px !important;
  }
}
</style>
